import svgPathBounds from "svg-path-bounds";
import { config } from "../config/config";
import { staticImages } from "../assets/icons/svg-icons";
import { ORDER_PROGRESS_COLORS, ORDER_STATUS_COLORS } from "../constants/constant";
import dayjs from "dayjs";

export const computeViewBox = (paths) => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    paths.forEach((d) => {
        try {
            const [x1, y1, x2, y2] = svgPathBounds(d);
            minX = Math.min(minX, x1);
            minY = Math.min(minY, y1);
            maxX = Math.max(maxX, x2);
            maxY = Math.max(maxY, y2);
        } catch (error) {
            // console.error("Invalid SVG path:", d, error);
        }
    });

    if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
        return "0 0 24 24"; // Default fallback
    }

    return `${minX} ${minY} ${maxX - minX} ${maxY - minY}`;
};

export const getImageURL = (path) => {
    if (path) {
        if (path.includes(config.apiBaseURL)) {
            return path;
        } else {
            return `${config.apiBaseURL}/${path}`;
        }
    } else {
        return `../${staticImages.defaultProfileImage}`;  // Return default image path
    }
}

export const convertIntoPascalCase = (input) => {
    return input
        .split('_') // Split the string by underscores
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
        .join(' '); // Join the words with a space
}

export const getRolePath = (role) => role?.toLowerCase().replace(/ /g, '-');  // Converts backend role (snake_case) to URL path (kebab-case)

/**
 * Format date to user-friendly format
 * @param {string|Date} dateInput - Date string or Date object
 * @param {string} format - Format type: 'long' (default), 'short', 'medium'
 * @returns {string} Formatted date string
 */
export const formatDate = (dateInput, format = 'long') => {
    if (!dateInput) return '-';

    const date = new Date(dateInput);

    // Check if date is valid
    if (isNaN(date.getTime())) return '-';

    const formatOptions = {
        long: {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }, // "10 July, 2022"
        short: {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }, // "10/07/2022"
        medium: {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        } // "10 Jul, 2022"
    };

    return date.toLocaleDateString('en-US', formatOptions[format] || formatOptions.long);
};

// Date conversion utility
export const convertDateForAPI = (date) => {
    if (!date) {
        return dayjs().format("YYYY-MM-DD");
    }

    // If Date object
    if (date instanceof Date) {
        return dayjs(date).format("YYYY-MM-DD");
    }

    // If string in DD/MM/YYYY
    if (typeof date === "string") {
        const parsed = dayjs(date, ["DD/MM/YYYY", "DD-MM-YYYY"], true);

        if (parsed.isValid()) {
            return parsed.format("YYYY-MM-DD");
        }

        // Already YYYY-MM-DD
        return date;
    }

    return "";
};

export const formatDateForInput = (dateInput) => {
    if (!dateInput) return '';
    return new Date(dateInput).toISOString().slice(0, 10);
};
/**
 * Get date label for today/yesterday
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} 'Today', 'Yesterday', or empty string
 */
export const getDateLabel = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
        return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
    }
    return '';
};

export const getStatusColor = (status) => {
    return ORDER_STATUS_COLORS[status] || ORDER_STATUS_COLORS.default;
};

export const getProgressColor = (progress) => {
    if (progress >= 70) return ORDER_PROGRESS_COLORS.high;
    if (progress >= 40) return ORDER_PROGRESS_COLORS.medium;
    return ORDER_PROGRESS_COLORS.low;
};